import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Stack,
  Chip,
  Typography,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarMonth as CalendarIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Place as PlaceIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import {
  SiteSchedule,
  ScheduleEventType,
  ScheduleStatus,
  EVENT_TYPE_LABEL,
  SCHEDULE_STATUS_LABEL,
} from '../../../models/SiteSchedule';
import RichTextEditor from '../../../components/editor/RichTextEditor';
import ScheduleAttachments from '../../../components/attachments/ScheduleAttachments';
import { getUserInfo } from '../../../utils/auth';

interface ScheduleDetailDialogProps {
  open: boolean;
  schedule: SiteSchedule | null;
  onClose: () => void;
  /** 제공 시 다이얼로그 우측 상단에 수정 버튼 노출. */
  onEdit?: (s: SiteSchedule) => void;
  /** 제공 시 다이얼로그 우측 상단에 삭제 버튼 노출. */
  onDelete?: (s: SiteSchedule) => void;
}

const EVENT_COLOR: Record<ScheduleEventType, string> = {
  visit: '#1976d2',
  maintenance: '#0288d1',
  incident: '#d32f2f',
  meeting: '#7b1fa2',
  worklog: '#f57c00',
  // 메모는 청록 — 기존 6색(파랑/하늘/빨강/보라/주황/회색) 어디와도 겹치지 않게.
  memo: '#00897b',
  other: '#616161',
};

const STATUS_CHIP_COLOR: Record<
  ScheduleStatus,
  'default' | 'info' | 'success' | 'warning'
> = {
  planned: 'info',
  in_progress: 'warning',
  done: 'success',
  cancelled: 'default',
};

const formatDateTimeLong = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDateTimeShort = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * 일정 상세 보기 다이얼로그. 리스트의 항목을 클릭하면 띄워서 전체 필드를 한눈에 본다.
 *
 * onEdit/onDelete 가 제공되면 우측 상단에 액션 버튼 노출.
 * 미제공이면 (대시보드 인라인 섹션처럼) 보기 전용.
 */
const ScheduleDetailDialog: React.FC<ScheduleDetailDialogProps> = ({
  open,
  schedule,
  onClose,
  onEdit,
  onDelete,
}) => {
  // 첨부 올리기·지우기는 admin/editor 만 — 백엔드 문서 API 권한과 같다.
  // 보기·내려받기는 로그인한 사람 모두 가능하다(공유가 목적이다).
  const role = getUserInfo()?.role;
  const attachWritable = role === 'admin' || role === 'editor';

  if (!schedule) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            일정이 선택되지 않았습니다.
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  const hasEndAt = !!schedule.scheduled_end_at;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <CalendarIcon fontSize="small" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {schedule.title}
          </Typography>
        </Box>
        {onEdit && (
          <IconButton
            size="small"
            onClick={() => onEdit(schedule)}
            aria-label="수정"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
        {onDelete && (
          <IconButton
            size="small"
            color="error"
            onClick={() => onDelete(schedule)}
            aria-label="삭제"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton onClick={onClose} size="small" aria-label="닫기">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* 메타 chip 줄 */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          sx={{ rowGap: 0.5, mb: 2 }}
        >
          <Chip
            label={EVENT_TYPE_LABEL[schedule.event_type]}
            size="small"
            sx={{ bgcolor: EVENT_COLOR[schedule.event_type], color: 'white' }}
          />
          <Chip
            label={SCHEDULE_STATUS_LABEL[schedule.status]}
            size="small"
            color={STATUS_CHIP_COLOR[schedule.status]}
            variant="outlined"
          />
        </Stack>

        {/* 일시 */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <ScheduleIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              일시
            </Typography>
          </Stack>
          <Typography
            variant="body2"
            sx={{ pl: 3.5, ...(schedule.scheduled_at ? null : { fontStyle: 'italic', color: 'text.secondary' }) }}
          >
            {schedule.scheduled_at ? formatDateTimeLong(schedule.scheduled_at) : '날짜 없음 (메모)'}
            {hasEndAt && (
              <>
                <Typography component="span" variant="caption" color="text.secondary" sx={{ mx: 0.75 }}>
                  →
                </Typography>
                {formatDateTimeLong(schedule.scheduled_end_at)}
              </>
            )}
          </Typography>
        </Box>

        {(schedule.assignee || schedule.location) && (
          <Box sx={{ mb: 2 }}>
            {schedule.assignee && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ minWidth: 56 }}>
                  담당자
                </Typography>
                <Typography variant="body2">{schedule.assignee}</Typography>
              </Stack>
            )}
            {schedule.location && (
              <Stack direction="row" spacing={1} alignItems="center">
                <PlaceIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ minWidth: 56 }}>
                  위치
                </Typography>
                <Typography variant="body2">{schedule.location}</Typography>
              </Stack>
            )}
          </Box>
        )}

        {schedule.description && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ display: 'block', mb: 0.5 }}
              >
                설명
              </Typography>
              <Box sx={{ '& .ProseMirror': { fontSize: '0.875rem' } }}>
                <RichTextEditor value={schedule.description} readOnly />
              </Box>
            </Box>
          </>
        )}

        {/* 첨부 — 공지사항을 서로 공유하려면 파일·이미지가 붙어야 한다.
            저장소는 현장 파일 보관함과 같고 schedule_id 로만 구분된다. */}
        {schedule.site_project_id ? (
          <>
            <Divider sx={{ my: 1.5 }} />
            <ScheduleAttachments
              scheduleId={schedule.id}
              projectId={schedule.site_project_id}
              writable={attachWritable}
            />
          </>
        ) : null}

        <Divider sx={{ my: 1.5 }} />
        <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ rowGap: 0.25 }}>
          <Typography variant="caption" color="text.secondary">
            등록: {formatDateTimeShort(schedule.created_at)}
          </Typography>
          {schedule.updated_at && schedule.updated_at !== schedule.created_at && (
            <Typography variant="caption" color="text.secondary">
              수정: {formatDateTimeShort(schedule.updated_at)}
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleDetailDialog;
